import { groupBy } from 'lodash';
import PoDetail from '@/store/modules/poDetails/constants';
import PurchaseOrderLineStatus from '@/models/purchaseOrderLineStatus';
import PurchaseOrderService from '@/services/purchaseOrderService';

import { getGroupNumber } from '@/helpers/skuHelper';

const purchaseOrderService = new PurchaseOrderService();

export default {
  [PoDetail.actionTypes.reportIssue]({ state, commit, getters }, payload) {
    const toUpdate = payload.lineItemIds || getters.currentLineItemInfo.lineItems.map((li) => li.id);
    const { poDetailsLink, notes, currentLineItemId } = payload;

    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    return purchaseOrderService.reportPoLineItemsIssue(state.poNumber, {
      lineItemIds: toUpdate,
      purchaseOrderRelativeLink: poDetailsLink,
      notes,
      currentLineItemId,
    })
      .then((response) => {
        const { reportedIssueLineItemIds, events } = response;

        const lineItemIds = getters.currentLineItemInfo.lineItems.map((li) => li.id);
        const filteredEvents = events.lineItemUpdateEvents
          .filter((li) => lineItemIds.includes(li.purchaseOrderLineItemId));

        if (filteredEvents.length > 0) {
          const currentActivities = state.currentLineItemActivities || [];
          const [firstEvent] = filteredEvents;

          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivityCanAddComment,
            events.canAddComment,
          );
          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivityIsAssignedToClient,
            events.isAssignedToClient,
          );
          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivity,
            [firstEvent, ...currentActivities],
          );
        }

        return Promise.resolve(reportedIssueLineItemIds.length);
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.getApprovalDocument]({ state, commit }, payload) {
    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    const lineItemIds = payload.lineItemIds || [];

    return purchaseOrderService.getApprovalDocument(state.poNumber, lineItemIds)
      .then((pdfBlob) => {
        const fileName = `purchase_order_${state.poNumber}.pdf`;

        return Promise.resolve({
          pdfBlob,
          fileName,
        });
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.updateLineItemsStatus]({
    dispatch,
    commit,
    state,
    getters,
  }, payload) {
    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    const toUpdate = payload.lineItemIds || getters.currentLineItemInfo.lineItems.map((li) => li.id);
    const {
      newStatus, poDetailsLink, notes, currentLineItemId,
    } = payload;

    return purchaseOrderService.updatePoLineItemsStatus(state.poNumber, {
      lineItemIds: toUpdate,
      purchaseOrderRelativeLink: poDetailsLink,
      newStatus,
      notes,
      currentLineItemId,
    })
      .then((response) => {
        const { updatedIds, events } = response;

        const lineItemIds = getters.currentLineItemInfo.lineItems.map((li) => li.id);
        const filteredEvents = events.lineItemUpdateEvents
          .filter((li) => lineItemIds.includes(li.purchaseOrderLineItemId));

        if (filteredEvents.length > 0) {
          const currentActivities = state.currentLineItemActivities || [];
          const [firstEvent] = filteredEvents;

          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivityCanAddComment,
            events.canAddComment,
          );
          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivityIsAssignedToClient,
            events.isAssignedToClient,
          );
          commit(
            PoDetail.mutationTypes.setCurrentLineItemActivity,
            [firstEvent, ...currentActivities],
          );
        }

        const updatedItemNumbers = state.groupedLineItems
          .filter((info) => info.lineItems.map((x) => x.id).find((id) => updatedIds.includes(id)))
          .map((info) => info.itemNumber);

        dispatch(PoDetail.actionTypes.syncLineItemsStatus, {
          toUpdate: [...updatedItemNumbers],
          status: newStatus,
        });

        dispatch(PoDetail.actionTypes.loadLineItemApprovedImages, {
          poNumber: state.poNumber,
          lineItemId: getters.currentLineItemInfo.lineItems[0].id,
        });

        return Promise.resolve(updatedIds.length);
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.uploadImagesMultiple]({
    commit, getters, state, dispatch,
  }, files) {
    const { lineItems } = getters.currentLineItemInfo;
    commit(PoDetail.mutationTypes.setImagesUploading, true);
    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    return purchaseOrderService
      .uploadLineItemsImages(
        files,
        state.poNumber,
        lineItems.map((li) => li.id),
      )
      .then((response) => {
        if (response.length > 0) {
          const images = state.currentLineItemUploadedImages || [];
          const { imageInfos, lineItemStatus } = response[0];

          commit(
            PoDetail.mutationTypes.setCurrentLineItemUploadedImages,
            [...images, ...imageInfos],
          );

          dispatch(PoDetail.actionTypes.syncLineItemsStatus, {
            toUpdate: [state.currentGroupLineItemNumber],
            status: lineItemStatus,
          });

          return Promise.resolve(imageInfos.length);
        }

        return Promise.resolve(0);
      })
      .catch((err) => Promise.reject(err))
      .finally(() => {
        commit(PoDetail.mutationTypes.setImagesUploading, false);
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.uploadImages]({
    commit, getters, state, dispatch,
  }, files) {
    const { sku } = getters.currentLineItemInfo;
    commit(PoDetail.mutationTypes.setImagesUploading, true);
    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    return purchaseOrderService
      .uploadLineItemImages(
        files,
        state.poNumber,
        sku,
        state.currentLineItemId,
      )
      .then((response) => {
        const images = state.currentLineItemUploadedImages || [];
        const { imageInfos, lineItemStatus } = response;

        commit(
          PoDetail.mutationTypes.setCurrentLineItemUploadedImages,
          [...images, ...imageInfos],
        );

        dispatch(PoDetail.actionTypes.syncLineItemsStatus, {
          toUpdate: [state.currentLineItemId],
          status: lineItemStatus,
        });

        return Promise.resolve(imageInfos.length);
      })
      .catch((err) => Promise.reject(err))
      .finally(() => {
        commit(PoDetail.mutationTypes.setImagesUploading, false);
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.isPoExists](vuex, poNumber) {
    return purchaseOrderService.isPurchaseOrderExists(poNumber)
      .then((result) => Promise.resolve(result))
      .catch(() => Promise.resolve(false));
  },

  async [PoDetail.actionTypes.loadOrderToList]({ commit }, poNumber) {
    const r = await purchaseOrderService.getPurchaseOrder(poNumber)
      .then((result) => Promise.resolve(result))
      .catch(() => Promise.resolve(false));
    const purchaseOrder = r.data;
    const result = {
      pageNumber: 1,
      totalCount: 1,
      purchaseOrderSearchResults: [{
        purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
        purchaseOrderSearchResultItems: purchaseOrder.purchaseOrderLines.map((item) => ({
          purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
          sku: item.sku,
          itemNumber: item.itemNumber,
        })),
      },
      ],
    };
    commit(PoDetail.mutationTypes.searchFinished, {
      searchText: result.PurchaseOrderNumber,
      searchPage: result,
    });
  },

  async [PoDetail.actionTypes.clearResults]({ commit }) {
    commit(PoDetail.mutationTypes.clearResults);
  },

  [PoDetail.actionTypes.onCurrentLineItemChanged]({ commit, state, dispatch }, itemNumber) {
    const { groupedLineItems } = state;
    let itemId = null;
    const tempItemNumber = getGroupNumber(groupedLineItems, itemNumber);

    for (let i = 0; i < groupedLineItems.length; i += 1) {
      const groupedLineItem = groupedLineItems[i];

      const isSelected = groupedLineItem.itemNumber === tempItemNumber;

      if (isSelected) itemId = groupedLineItem.lineItems[0].id;

      groupedLineItem.selected = isSelected;
    }

    const lineItemInfo = {
      poNumber: state.poNumber,
      lineItemId: itemId,
    };

    commit(PoDetail.mutationTypes.setPoGroupedLineItems, groupedLineItems);
    commit(PoDetail.mutationTypes.setCurrentGroupLineItemNumber, itemNumber);

    dispatch(PoDetail.actionTypes.loadLineItemUploadedImages, lineItemInfo);
    dispatch(PoDetail.actionTypes.loadLineItemApprovedImages, lineItemInfo);
    dispatch(PoDetail.actionTypes.loadLineItemActivitiy, lineItemInfo);
  },

  [PoDetail.actionTypes.loadPoDetails]({ commit, dispatch, state }, poNumber) {
    commit(PoDetail.mutationTypes.setLoading, true);
    commit(PoDetail.mutationTypes.setPoNumber, poNumber);

    if (state.poNumber !== poNumber) {
      dispatch(PoDetail.actionTypes.resetLineItemInfo);
      commit(PoDetail.mutationTypes.setPoGroupedLineItems, []);
      const clientInfo = {
        clients: '',
        isMyClient: false,
        isMyEmployeeClient: false,
      };
      commit(PoDetail.mutationTypes.setClients, clientInfo);
    }

    purchaseOrderService
      .getPurchaseOrder(poNumber)
      .then((response) => {
        const clientInfo = {
          clients: response.data.clients,
          isMyClient: response.data.hasMyClient,
          isMyEmployeeClient: response.data.hasMyEmployeeClient,
        };
        commit(PoDetail.mutationTypes.setClients, clientInfo);
        dispatch(PoDetail.actionTypes.processLineItems, response.data.purchaseOrderLines);
      })
      .catch(() => {
        commit(PoDetail.mutationTypes.setPoGroupedLineItems, []);
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setLoading, false);
      });
  },

  [PoDetail.actionTypes.processLineItems]({ commit, state }, poLineItems) {
    const groupedLineItems = groupBy(poLineItems, (li) => li.itemNumber);

    const entries = Object.entries(groupedLineItems);

    const lineItems = [];

    for (let i = 0; i < entries.length; i += 1) {
      const [itemNumber, items] = entries[i];
      const {
        status, client, isMyClient, isMyEmployeeClient,
      } = items[0];
      const selected = itemNumber === state.currentGroupLineItemNumber;

      lineItems.push({
        itemNumber,
        status,
        client,
        isMyClient,
        isMyEmployeeClient,
        lineItems: items.map((item) => ({
          ...item,
          boxes: [
            {
              quantity: 1,
              quantityInput: '1',
              sscc: '',
            },
          ],
        })),
        selected,
      });
    }

    commit(PoDetail.mutationTypes.setPoGroupedLineItems, lineItems);
  },

  [PoDetail.actionTypes.loadLineItemUploadedImages]({ commit }, payload) {
    commit(PoDetail.mutationTypes.setUploadedImagesLoading, true);
    const { poNumber, lineItemId } = payload;

    purchaseOrderService.getPurchaseOrderLineItemUploadedImages(poNumber, lineItemId)
      .then((result) => {
        commit(
          PoDetail.mutationTypes.setCurrentLineItemUploadedImages,
          result,
        );
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setUploadedImagesLoading, false);
      });
  },

  [PoDetail.actionTypes.loadLineItemApprovedImages]({ commit }, payload) {
    commit(PoDetail.mutationTypes.setApprovedImagesLoading, true);
    const { poNumber, lineItemId } = payload;

    purchaseOrderService.getPurchaseOrderLineItemApprovedImages(poNumber, lineItemId)
      .then((result) => {
        commit(
          PoDetail.mutationTypes.setCurrentLineItemApprovedImages,
          result,
        );
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setApprovedImagesLoading, false);
      });
  },

  [PoDetail.actionTypes.loadLineItemActivitiy]({ commit }, payload) {
    commit(PoDetail.mutationTypes.setActivityLoading, true);
    commit(PoDetail.mutationTypes.setCurrentLineItemActivity, []);

    const { poNumber, lineItemId } = payload;

    purchaseOrderService.getPurchaseOrderLineItemActivity(poNumber, lineItemId)
      .then((result) => {
        commit(PoDetail.mutationTypes.setCurrentLineItemActivity, result.lineItemUpdateEvents);
        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivityCanAddComment,
          result.canAddComment,
        );
        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivityIsAssignedToClient,
          result.isAssignedToClient,
        );
        commit(PoDetail.mutationTypes.setActivityLoading, false);
      })
      .catch(() => {
        commit(PoDetail.mutationTypes.setActivityLoading, false);
      });
  },

  [PoDetail.actionTypes.checkCanAddComment]({ state, commit }, payload) {
    const { lineItemId } = payload;
    commit(PoDetail.mutationTypes.setActionsDisabled, true);
    return purchaseOrderService.getCanAddComment(state.poNumber, lineItemId)
      .then((result) => {
        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivityCanAddComment,
          result,
        );

        return Promise.resolve(result);
      })
      .catch(() => Promise.resolve(false))
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.resetLineItemInfo]({ commit }) {
    commit(PoDetail.mutationTypes.setCurrentGroupLineItemNumber, null);
    commit(PoDetail.mutationTypes.setCurrentLineItemUploadedImages, []);
    commit(PoDetail.mutationTypes.setCurrentLineItemApprovedImages, []);
    commit(PoDetail.mutationTypes.setCurrentLineItemActivity, []);
  },

  [PoDetail.actionTypes.syncLineItemsStatus]({ commit, state }, payload) {
    const { groupedLineItems } = state;
    const { toUpdate, status } = payload;

    if (PurchaseOrderLineStatus.UnderReview.is(status)) {
      commit(PoDetail.mutationTypes.setCurrentLineItemUploadedImages, []);
    }

    for (let i = 0; i < toUpdate.length; i += 1) {
      const itemNumber = toUpdate[i];
      const lineItemsGroup = groupedLineItems.find((li) => li.itemNumber === itemNumber);

      if (lineItemsGroup) {
        lineItemsGroup.status = status;
        for (let j = 0; j < lineItemsGroup.lineItems.length; j += 1) {
          const lineItem = lineItemsGroup.lineItems[j];

          lineItem.status = status;
        }
      }
    }

    commit(PoDetail.mutationTypes.setPoGroupedLineItems, groupedLineItems);
  },

  [PoDetail.actionTypes.deleteImage]({
    commit, getters, dispatch, state,
  }, imageId) {
    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    const { sku } = getters.currentLineItemInfo;

    return purchaseOrderService
      .deleteLineItemImage(imageId, state.poNumber, sku, state.currentLineItemId)
      .then((data) => {
        const { lineItemStatus } = data;
        const images = state.currentLineItemUploadedImages || [];

        commit(
          PoDetail.mutationTypes.setCurrentLineItemUploadedImages,
          images.filter((image) => image.id !== imageId),
        );

        if (lineItemStatus) {
          dispatch(PoDetail.actionTypes.syncLineItemsStatus, {
            toUpdate: [state.currentLineItemId],
            status: lineItemStatus,
          });
        }

        return Promise.resolve();
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },

  [PoDetail.actionTypes.deleteImageMultiple]({
    commit, getters, dispatch, state,
  }, imageId) {
    commit(PoDetail.mutationTypes.setActionsDisabled, true);
    const lineItemIds = getters.currentLineItemInfo.lineItems.map((li) => li.id);

    return purchaseOrderService
      .deleteLineItemImageMultiple(imageId, state.poNumber, lineItemIds)
      .then((data) => {
        const { lineItemStatus } = data;
        const images = state.currentLineItemUploadedImages || [];

        commit(
          PoDetail.mutationTypes.setCurrentLineItemUploadedImages,
          images.filter((image) => image.id !== imageId),
        );

        if (lineItemStatus) {
          dispatch(PoDetail.actionTypes.syncLineItemsStatus, {
            toUpdate: [state.currentGroupLineItemNumber],
            status: lineItemStatus,
          });
        }
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },
  [PoDetail.actionTypes.deleteCommentMultiple]({
    commit, getters, state,
  }, commentId) {
    commit(PoDetail.mutationTypes.setActionsDisabled, true);
    const lineItemIds = getters.currentLineItemInfo.lineItems.map((li) => li.id);

    return purchaseOrderService
      .deleteLineItemCommentMultiple(commentId, state.poNumber, lineItemIds)
      .then(() => {
        const currentActivities = state.currentLineItemActivities || [];

        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivity,
          currentActivities.filter((comment) => comment.commentId !== commentId),
        );

        return Promise.resolve();
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },
  [PoDetail.actionTypes.itemCommented]({ state, commit, getters }, payload) {
    const toUpdate = payload.lineItemIds || getters.currentLineItemInfo.lineItems.map((li) => li.id);
    const { poDetailsLink, notes } = payload;

    commit(PoDetail.mutationTypes.setActionsDisabled, true);

    return purchaseOrderService.addComments(state.poNumber, {
      lineItemIds: toUpdate,
      purchaseOrderRelativeLink: poDetailsLink,
      notes,
    })
      .then((response) => {
        const { commentedLineItemIds, events } = response;
        commit(PoDetail.mutationTypes.setCurrentLineItemActivity, events.lineItemUpdateEvents);
        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivityCanAddComment,
          events.canAddComment,
        );
        commit(
          PoDetail.mutationTypes.setCurrentLineItemActivityIsAssignedToClient,
          events.isAssignedToClient,
        );
        commit(PoDetail.mutationTypes.setActivityLoading, false);

        return Promise.resolve(commentedLineItemIds.length);
      })
      .finally(() => {
        commit(PoDetail.mutationTypes.setActionsDisabled, false);
      });
  },
};
